import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { setCsrfToken, getQueryFn } from "@/lib/queryClient";

interface Session {
  authenticated: boolean;
  user?: {
    id: number;
    username: string;
  };
  csrfToken?: string;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const [, setLocation] = useLocation();

  const { data: session, isLoading } = useQuery<Session | null>({
    queryKey: ["/api/admin/auth/session"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    staleTime: 0,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (!isLoading && !session?.authenticated) {
      setLocation("/admin/login");
    } else if (session?.authenticated && session?.csrfToken) {
      // Store CSRF token for authenticated sessions
      setCsrfToken(session.csrfToken);
    }
  }, [session, isLoading, setLocation]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session?.authenticated) {
    return null;
  }

  return <>{children}</>;
}
